import { reactive } from 'vue'
import { debounce } from 'util/ts-utils'
import { Filter as IFilter, FilterOption, SummaryDimension } from 'services/api-service'
import msg from 'services/msg'

/**
 * Place to store mutable state pertaining to filters.
 * This is used to store the filters applied to the list of reviews.  It can be used to create
 * instances used for other filters in the future if needed.
 * The `state` property within the store class contains the
 * actual data that changes, access should be done through the
 * getters with mutation done through the setters or other action functions.
 *
 * @see https://v3.vuejs.org/guide/state-management.html#simple-state-management-from-scratch
 */

/**
 * A structure that allows quick access to an option for a particular filter
 * by group and filter name and used by listboxes to maintain state.
 */
export interface IFilterValues {
  [group: string]: {
    [filterName: string]: unknown // string | number | boolean | Array<string> | Array<number>
  }
}

/**
 * Represents an object in a list of applied filters.  Used for displaying chips for each
 * applied filter.
 */
export interface IAppliedFilter {
  filter: IFilter
  value: unknown
  label: string
}

/**
 * Interface for the state property inside the store
 */
export interface IFiltersState {
  appliedFilters: Array<IAppliedFilter>
  filterValues: IFilterValues
  filters: Array<IFilter>
  encodedFiltersUrl: string
}

// when initializing the filterValues for each filter, setting to one of these values based on
// filter type
function getDefaultValForType(type: 'boolean' | 'string' | 'number' | 'array') {
  return type === 'array' ? [] : null
}

function getRatingOptions(): FilterOption[] {
  return [5, 4, 3, 2, 1].map((r) => ({ value: r, labelKey: `filter.rt.${r}star${r > 1 ? 's' : ''}` }))
}

export class FilterStore {
  state: IFiltersState = reactive({
    appliedFilters: [], // holds values for those that are selected in order
    filterValues: {}, // holds the values for all filters, used by listboxes (or other control)
    filters: [],
    // an encoded, ordered json obj for sending in query path
    encodedFiltersUrl: encodeURIComponent('{}')
  })

  get filterValues(): IFilterValues {
    return this.state.filterValues
  }

  clearFilters(): void {
    this.state.filters.forEach((filter) => {
      if (this.state.filterValues[filter.group]) {
        this.state.filterValues[filter.group][filter.name] = getDefaultValForType(filter.type)
      }
    })
    this.state.appliedFilters = []
    this.state.encodedFiltersUrl = encodeURIComponent('{}')
  }

  get appliedFilters(): Array<IAppliedFilter> {
    return this.state.appliedFilters
  }

  get encodedFiltersUrl(): string {
    return this.state.encodedFiltersUrl
  }

  findFilter(group: string, name: string): IFilter {
    return this.state.filters.find((f: IFilter) => {
      return f.group === group && f.name === name
    })
  }

  setFilterValue(filter: IFilter, value: unknown): void {
    this.state.filterValues[filter.group][filter.name] = value
    this.updateAppliedFilters()
  }

  // only to be called internally.  debounced because it can be called multiple times when
  // clearing filters and additional change events fired by listbox when selections are made
  // external to listbox
  updateAppliedFilters = debounce(function cb() {
    const filterValues = this.state.filterValues
    const appliedFilters = [] as Array<IAppliedFilter>
    const appliedFiltersMap = {}
    // Need to alphabetize the output (JSON.stringify) below so that the filter is consistent
    // for CDN caching. Idea for how:
    // https://stackoverflow.com/questions/16167581/sort-object-properties-and-json-stringify
    const replacer = []
    this.state.filters.forEach((filter) => {
      const group = filter.group
      const filterName = filter.name
      const filterValue = filterValues[group][filterName]
      if ((filter.type === 'array' && filterValue.length > 0) ||
          (filter.type !== 'array' && (!!filterValue || filterValue === 0))) {
        if (replacer.indexOf(group) === -1) {
          replacer.push(group)
        }
        replacer.push(filterName)
        if (!appliedFiltersMap[group]) {
          appliedFiltersMap[group] = {}
        }
        appliedFiltersMap[group][filterName] = filterValue
        const optionValues = filter.type === 'array' ? filterValue : [filterValue]
        const filterLabel = filter.group === 'cm' ? '' :
          `${(filter.labelKey ? msg(filter.labelKey) : filter.label)}: `
        optionValues.forEach((optionValue) => {
          const option = filter.options.find(opt => opt.value === optionValue)
          appliedFilters.push({
            filter,
            value: option.value,
            label: `${filterLabel}${option.labelKey ? msg(option.labelKey) : option.label}`
          })
        })
      }
    })
    this.state.appliedFilters = appliedFilters
    this.state.encodedFiltersUrl =
      encodeURIComponent(JSON.stringify(appliedFiltersMap, replacer.sort()))
  }, 50)

  removeFilterValue(filter: IFilter, value: unknown): void {
    if (filter.type === 'array') {
      const valueArray = this.state.filterValues[filter.group][filter.name] as []
      // setFilterValue needs the full array with this given value removed
      this.setFilterValue(filter, valueArray.filter(v => v !== value))
      return
    }
    this.setFilterValue(filter, getDefaultValForType(filter.type))
  }

  /**
   * Set the filters to be used.  This also initializes the filterValues for each filter
   */
  set filters(filters: Array<IFilter>) {
    // in case being reset, clear state values
    this.state.filters = []
    this.clearFilters()
    // organize filters and filterValues by group / filter name
    filters.forEach((filter) => {
      this.addFilter(filter)
    })
  }

  get filters(): Array<IFilter> {
    return this.state.filters
  }

  // adds to the internal list of filters
  addFilter(filter: IFilter): void {
    this.state.filters.push(filter)
    if (!this.state.filters[filter.group]) {
      this.state.filters[filter.group] = {}
      this.state.filterValues[filter.group] = {}
    }
    this.state.filters[filter.group][filter.name] = filter
    this.state.filterValues[filter.group][filter.name] = getDefaultValForType(filter.type)
  }

  applySingleFilter(group: string, name: string, value: unknown, dim: SummaryDimension): void {
    const filter = this.findOrAddFilter(group, name, dim)
    this.clearFilters()
    this.setFilterValue(filter, [value])
  }

  // intended for filter added from Reviews Summary.  One that may or may not be part of the
  // filter set for the list
  findOrAddFilter(group: string, name: string, dim: SummaryDimension): IFilter {
    const filter = this.findFilter(group, name)
    if (filter) {
      return filter
    }
    const isRatingFilter = group === 'cm' && name === 'rt'
    // at this time, there is only single/multi-select dim, so some data points can be assumed
    const filterToAdd = {
      name,
      group,
      hidden: true,
      label: !isRatingFilter ? dim.label : null,
      labelKey: isRatingFilter ? 'filter.rt.label' : null,
      type: 'array',
      options: isRatingFilter ? getRatingOptions() : dim.values.map((dv) => {
        return {
          value: dv.id,
          label: dv.label
        }
      })
    } as IFilter
    this.addFilter(filterToAdd)
    return filterToAdd
  }
}
